// app.jsx — provider stack + routes.
//   BrowserRouter > AuthProvider > ThemeProvider(+CssBaseline) > LangProvider > Routes
// Public:  /login
// Guarded: everything else, via TWO layout routes (SB-042 — the six per-route
// <AuthGuard> wrappers collapsed into one guarded layout + one admin-only layout;
// AuthGuard renders the AppShell chrome around an <Outlet/>).
// /  redirects to /docs (which is itself guarded, so signed-out users land on /login).
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme.mjs';
import { LangProvider } from './i18n/index.mjs';
import { AuthProvider } from './auth/AuthContext.jsx';
import AuthGuard from './components/AuthGuard.jsx';
import LoginPage from './pages/LoginPage.jsx';
import LoginLinkPage from './pages/LoginLinkPage.jsx';
import DocBrowserPage from './pages/DocBrowserPage.jsx';
import SubmitTicketPage from './pages/SubmitTicketPage.jsx';
import MyTicketsPage from './pages/MyTicketsPage.jsx';
import ApprovalsPage from './pages/ApprovalsPage.jsx';
import AdminAccountsPage from './pages/AdminAccountsPage.jsx';
import AdminGitAccessPage from './pages/AdminGitAccessPage.jsx';
import AdminApiKeysPage from './pages/AdminApiKeysPage.jsx';
import AdminSettingsPage from './pages/AdminSettingsPage.jsx';
import AdminReposPage from './pages/AdminReposPage.jsx';
import AdminLogsPage from './pages/AdminLogsPage.jsx';
import AdminBurnPage from './pages/AdminBurnPage.jsx';
import AdminEscalationsPage from './pages/AdminEscalationsPage.jsx';
import AdminReadAuditPage from './pages/AdminReadAuditPage.jsx';
import AdminResponseSlaPage from './pages/AdminResponseSlaPage.jsx';
import WorkHoursPage from './pages/WorkHoursPage.jsx';
import WorkItemsPage from './pages/WorkItemsPage.jsx';
import ReportsPage from './pages/ReportsPage.jsx';
import DueActionsPage from './pages/DueActionsPage.jsx';
import AssetProgressPage from './pages/AssetProgressPage.jsx';
import FeatureProgressPage from './pages/FeatureProgressPage.jsx';
import ThreadsPage from './pages/ThreadsPage.jsx';
import MessagesPage from './pages/MessagesPage.jsx';
import SecretsPage from './pages/SecretsPage.jsx';
import CliDownloadPage from './pages/CliDownloadPage.jsx';
import HelpPage from './pages/HelpPage.jsx';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LangProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* 第八十七批：未读提醒信里那枚次抛登录链接的落地页。**公开**——
                  它存在的全部意义就是替代登录，挂在守卫后面就自相矛盾了。
                  漏掉这一行的表现是"点了信里的链接被弹回首页"，浏览器探针
                  login-link 以「地址还在不在 /login/link」咬住它。 */}
              <Route path="/login/link" element={<LoginLinkPage />} />
              <Route path="/" element={<Navigate to="/docs" replace />} />
              <Route element={<AuthGuard />}>
                <Route path="/docs" element={<DocBrowserPage />} />
                {/* Deep link to one document. Splat (not :param) — a docKey is a
                    multi-segment logical path. This route IS what publish.mjs bakes
                    into every in-document link, so doc-to-doc navigation, Back/Forward
                    and shareable URLs are one mechanism. */}
                <Route path="/docs/:group/*" element={<DocBrowserPage />} />
                <Route path="/submit" element={<SubmitTicketPage />} />
                <Route path="/my-tickets" element={<MyTicketsPage />} />
                {/* Batch 50 (SB-064 interim): nav entry is mayApprove-gated in
                    AppShell; the real boundary is the backend approve gate. */}
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/work-hours" element={<WorkHoursPage />} />
                <Route path="/work-items" element={<WorkItemsPage />} />
                {/* 第一百零二批（SB-106）：项目汇报读面。与 /work-items 同层（普通
                    守卫），因为它对每个登录账号可达——能不能看到内容由**组成员资格**
                    决定，而那是后端按 currentGroup 分片的数据边界（非成员一律 404），
                    不是一把要在路由上再表达一遍的锁。在这里加门等于把同一条规则放到
                    两处，而两处迟早会漂开。 */}
                <Route path="/reports" element={<ReportsPage />} />
                {/* SB-117 到期动作。入口主要是全局壳那条横幅上的按钮——
                    这一页是落定的地方，不是"撞见"的地方。 */}
                <Route path="/due-actions" element={<DueActionsPage />} />
                {/* SB-197 S3b（第二百九十二批）：资产对账表四态记录。普通守卫层——读闸在
                    服务端（能看台账页的岗位；看不见与组未发布同一句 404），记态的角色由
                    服务端按岗位派生、页内按钮显隐，不是一把要挂在路由上的锁。 */}
                <Route path="/asset-progress" element={<AssetProgressPage />} />
                {/* SB-201 S3（第三百零八批）：功能代码妥善率表三态记录。与上一条同一姿态——读闸在服务端
                    （能看功能表页的岗位），记态角色由服务端按岗位派生、页内按钮显隐。 */}
                <Route path="/feature-progress" element={<FeatureProgressPage />} />
                {/* 第一百零四批 B：组间通信。普通守卫层——读面对**执行层**每个登录
                    账号全量可见（透明口径，页内头注详述）；写面的 canLiaison 是页内
                    控件显隐 + 后端 403，不是一把要挂在路由上的锁。
                    第一百零八批 C1：非执行层（外包/外部顾问/资方）不可达此页，但那道闸
                    **刻意不做成第四个守卫层**：AuthGuard 的 adminOnly 走的是"弹回
                    /docs"，而弹走一个人不告诉他为什么，等于让他反复点同一个入口。
                    ThreadsPage 自己在取数之前渲染一句说得出理由的拒绝（页内 C1 段），
                    导航入口同时按同一个谓词隐藏。真边界在后端：整族 404。 */}
                <Route path="/threads" element={<ThreadsPage />} />
                {/* 第一百零九批 D：一对一站内信。**普通守卫层，没有第二道门**——
                    后端 routes_messages.cpp 明写"不要求 group_liaison：这是人对人的信，
                    不是代表组说话"，闸只有 require_session。可见性是逐行的数据边界
                    （我是收件人 或 我是发送方，其余一律 404），而那不是一把要在路由上
                    再表达一遍的锁：同一条规则放到两处，两处迟早会漂开。
                    与 /threads 的对照是刻意的：那一页的透明口径只对执行层成立，而一封
                    信对外包与外部顾问同样有用。 */}
                <Route path="/messages" element={<MessagesPage />} />
                {/* 第一百零九批 D：密件取件面。同为普通守卫层——它用的三只端点自
                    第一百零六批就是 require_session，可见集由后端逐行判。
                    ⚠ 这一页与 /cli 是**一对**：CLI 要一枚 key 才能跑、key 只能从密递取，
                    而密递此前只能用 CLI 取——「用 key 才能取到 key」。少任何一条路由，
                    一个新接入方在浏览器里都走不完「拿到工具 + 拿到钥匙」。 */}
                <Route path="/secrets" element={<SecretsPage />} />
                {/* 第一百零九批 D：CLI 整包下载页。**不进导航**（横向预算第一百零二/
                    一百零四批就用满了，而它是一次性入口），入口从 /help 链过去，
                    所以导航净增 0。挂在普通守卫层的理由与 /help 同一条：它要解释的第一
                    件事就是"你怎么开始"，把它锁在任何一把权限门后面就自相矛盾了。 */}
                <Route path="/cli" element={<CliDownloadPage />} />
                {/* 使用手册（/help）。刻意放在**普通守卫层**而不是任何门控层：它对每个
                    登录账号可达，与岗位无关——手册要解释的第一件事恰恰是"为什么你的岗位
                    看不到某些文档"，把它挂到任何一把权限锁后面就自相矛盾了。
                    内容在 pages/helpContent.mjs（结构化双语数据，配单测与后端源对账），
                    不进 data_content 文档树（那棵树按 view glob 授权且走发布管线）。 */}
                <Route path="/help" element={<HelpPage />} />
              </Route>
              <Route element={<AuthGuard adminOnly />}>
                <Route path="/admin" element={<AdminAccountsPage />} />
                <Route path="/admin/git" element={<AdminGitAccessPage />} />
                <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="/admin/repos" element={<AdminReposPage />} />
                {/* 第八十一批：运行日志。与其余 /admin/* 同层（adminOnly 守卫），
                    因为日志行里有请求路径、账号 id 与模型报错正文——它是全系统最不该
                    按人分片的东西之一。 */}
                <Route path="/admin/logs" element={<AdminLogsPage />} />
                {/* 第九十九批：工时燃耗对账（SB-104，仅管理员）。 */}
                <Route path="/admin/burn" element={<AdminBurnPage />} />
                {/* 第一百一十批：agent 逃生口（「我做不了」）的管理面。与 /admin/logs
                    同层（adminOnly）——它端出的是跨组、跨工单的系统数据：工单编号、
                    agent 的原话、工具留痕。这不是按组分片的东西，也不该按组分片：
                    「装配器缺料」这个结论只有横切着看才得出来。 */}
                <Route path="/admin/escalations" element={<AdminEscalationsPage />} />
                {/* SB-116（第一百二十一批）：敏感读面的读取记录（仅管理员）。
                    与 /admin/logs 同族——不改任何配置，但"这个资源谁读过"这个
                    取证问题只有这一页答得出（审计存了但查不到，等于没存）。 */}
                <Route path="/admin/read-audit" element={<AdminReadAuditPage />} />
                {/* SB-190 §7.3（第二百四十六批）：响应时限（03-scoring D4 ①）的读面与裁决台。
                    adminOnly：它端出跨组、跨账号的事项与裁决（响应方本人只在 /api/me 那一面
                    看自己的行，那是 dc-cli `sla mine` 与步 5 的成员面）。 */}
                <Route path="/admin/response-sla" element={<AdminResponseSlaPage />} />
              </Route>
              {/* Unknown client route → home (→ /docs → guard). */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LangProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
